/**
 * ReviewsList - Panel showing all reviews for a profile
 *
 * Matches Figma node 1350:15761:
 *   - Header: "Reviews" (20px semibold) + count (16px medium secondary), justify-between
 *   - Gap header→cards: 12px (gap-3)
 *   - Cards gap: 24px (gap-6)
 *
 * Used in both DeveloperProfilePage and ClientProfilePage.
 */

import { cn } from '@lib/cn';
import { ReviewCard } from './ReviewCard';
import { Spinner } from '@components/ui/Spinner';
export interface ProfileReview {
  id: string;
  rating: number;
  reviewerName: string;
  reviewerRole?: string;
  reviewerAvatar?: string;
}

export interface ReviewsListProps {
  ratings: ProfileReview[];
  totalCount: number;
  isLoading?: boolean;
  className?: string;
}

export function ReviewsList({
  ratings,
  totalCount,
  isLoading = false,
  className,
}: ReviewsListProps) {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-xl font-semibold text-[--text-dark-primary]">
          Reviews
        </span>
        <span className="text-base font-medium text-[--text-dark-secondary]">
          {totalCount}
        </span>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Spinner size="md" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && ratings.length === 0 && (
        <div className="rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-2] px-[--spacing-12] py-[--spacing-8] text-center">
          <p className="text-sm italic text-[--text-dark-secondary]">
            No reviews yet
          </p>
        </div>
      )}

      {/* Review cards */}
      {!isLoading && ratings.length > 0 && (
        <div className="flex flex-col gap-[--spacing-10]">
          {ratings.map((rating) => (
            <ReviewCard
              key={rating.id}
              reviewerName={rating.reviewerName}
              reviewerRole={rating.reviewerRole}
              reviewerAvatarUrl={rating.reviewerAvatar}
              rating={rating.rating}
            />
          ))}
        </div>
      )}
    </div>
  );
}
